import React, { forwardRef, useMemo } from 'react';
import ColorScaleLegend from '../ColorScaleLegend';
import {
  composePage, crsText, fitMapImage, formatScale, graticuleLines, metersPerPixel, niceScaleBar, northArrowRotation,
  placeFurniture, scaleDenominator,
} from '../../lib/print-layout';

const INK = '#11161d';
const MUTED = '#6e7681';
const box = { background: 'rgba(255,255,255,0.94)', border: '1px solid #8b949e', borderRadius: 4 };
const corner = (pos, inset = 10) => ({
  position: 'absolute',
  ...(pos.startsWith('top') ? { top: inset } : { bottom: inset }),
  ...(pos.endsWith('left') ? { left: inset } : { right: inset }),
});

export function NorthArrow({ bearing = 0 }) {
  return (
    <div data-testid="north-arrow" style={{ ...box, padding: '4px 6px', textAlign: 'center', width: 30 }}>
      <svg width="18" height="26" viewBox="0 0 18 26" style={{ transform: `rotate(${northArrowRotation(bearing)}deg)`, display: 'block', margin: '0 auto' }}>
        <polygon points="9,1 16,24 9,19 2,24" fill={INK} />
        <polygon points="9,1 9,19 2,24" fill="#ffffff" stroke={INK} strokeWidth="1" />
      </svg>
      <div style={{ fontSize: 11, fontWeight: 700, color: INK, lineHeight: 1 }}>N</div>
    </div>
  );
}

export function ScaleBar({ mpp, units }) {
  const bar = niceScaleBar(mpp, 180, units);
  if (!bar.lengthPx) return null;
  return (
    <div data-testid="scale-bar" style={{ ...box, padding: '4px 8px' }}>
      <div style={{ width: bar.lengthPx, height: 6, border: `1px solid ${INK}`, borderTop: 'none', borderBottom: `3px solid ${INK}`, boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: INK, marginTop: 2, width: bar.lengthPx }}>
        <span>0</span><span>{bar.label}</span>
      </div>
    </div>
  );
}

export function PrintLegend({ layers = [], colorScales = [] }) {
  if (!layers.length && !colorScales.length) return null;
  return (
    <div data-testid="print-legend" style={{ ...box, padding: '8px 10px', minWidth: 130, maxWidth: 190 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: INK, marginBottom: 4, textTransform: 'uppercase' }}>Legend</div>
      {colorScales.map((c, i) => <ColorScaleLegend key={i} inline title={c.title} min={c.min} max={c.max} colors={c.colors} />)}
      {layers.map((l) => (
        <div key={l.id || l.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <div style={{ width: 12, height: 8, background: l.color || '#00d4ff', border: '1px solid rgba(0,0,0,0.35)', borderRadius: 1, flexShrink: 0 }} />
          <span style={{ fontSize: 9, color: '#30363d' }}>{l.name}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The composed page at 96 dpi css px (width/height = pageDimensions). Everything the export
 * captures lives in this node; the preview wrapper's transform is removed for capture.
 *
 * Props: page {widthPx,heightPx}, opts {title, subtitle, units, legendPlacement, showLegend,
 *   showNorth, showScale, showGraticule, dateText, projectName}, capture (captureMap result|null),
 *   layers, colorScales, logo (data URL|null), crsLabel
 */
const PrintPage = forwardRef(function PrintPage({ page, opts, capture, layers, colorScales, logo, crsLabel }, ref) {
  const { map: frame, side } = composePage(page.widthPx, page.heightPx, opts.legendPlacement, opts.showLegend);
  const fit = useMemo(() => (capture ? fitMapImage({ mapW: capture.mapW, mapH: capture.mapH, frameW: frame.w, frameH: frame.h }) : null),
    [capture, frame.w, frame.h]);
  const mpp = capture ? metersPerPixel(capture.lat, capture.view.zoom) / fit.k : 0;
  const denominator = capture ? scaleDenominator(metersPerPixel(capture.lat, capture.view.zoom), fit.k) : 0;
  const view = capture ? { ...capture.view, k: fit.k } : null;
  const grat = capture && opts.showGraticule ? graticuleLines(view, { w: frame.w, h: frame.h }) : null;
  const fur = placeFurniture(opts.legendPlacement, opts.showLegend);
  const legendInCorner = opts.showLegend && !side;

  return (
    <div ref={ref} data-testid="print-page" style={{
      width: page.widthPx, height: page.heightPx, background: '#ffffff', position: 'relative', overflow: 'hidden',
      fontFamily: 'system-ui, sans-serif', color: INK, boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{ position: 'absolute', left: 24, right: 24, top: 24, height: 56, borderBottom: `2px solid ${INK}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15 }}>{opts.title}</div>
          {opts.subtitle && <div style={{ fontSize: 12, color: MUTED }}>{opts.subtitle}</div>}
        </div>
        {logo
          ? <img src={logo} alt="Logo" style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }} />
          : <div style={{ fontSize: 9, color: MUTED, textAlign: 'right' }}>Ultimate-GIS</div>}
      </div>

      {/* Map frame */}
      <div data-testid="print-map-frame" style={{ position: 'absolute', left: frame.x, top: frame.y, width: frame.w, height: frame.h, overflow: 'hidden', background: '#e2e8f0', border: `1px solid ${INK}`, boxSizing: 'border-box' }}>
        {capture
          ? <img src={capture.dataUrl} alt="Map" draggable={false} style={{ position: 'absolute', left: fit.left, top: fit.top, width: fit.width, height: fit.height, maxWidth: 'none' }} />
          : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: MUTED, fontSize: 13 }}>Capturing map…</div>}

        {grat && (
          <svg data-testid="graticule" width={frame.w} height={frame.h} style={{ position: 'absolute', left: 0, top: 0 }}>
            {grat.lines.map((l) => (
              <g key={`${l.axis}${l.value}`}>
                <line x1={l.p1[0]} y1={l.p1[1]} x2={l.p2[0]} y2={l.p2[1]} stroke="#ffffff" strokeOpacity="0.7" strokeWidth="2.2" />
                <line x1={l.p1[0]} y1={l.p1[1]} x2={l.p2[0]} y2={l.p2[1]} stroke={INK} strokeOpacity="0.75" strokeWidth="0.8" strokeDasharray="4 3" />
                <text x={l.labelAt[0] + (l.axis === 'lat' ? 4 : 3)} y={l.labelAt[1] + (l.axis === 'lat' ? -3 : -4)} fontSize="9" fill={INK} stroke="#ffffff" strokeWidth="2.5" paintOrder="stroke">{l.label}</text>
              </g>
            ))}
          </svg>
        )}

        {opts.showNorth && capture && <div style={corner(fur.north)}><NorthArrow bearing={capture.view.bearing} /></div>}
        {opts.showScale && capture && <div style={corner(fur.scale)}><ScaleBar mpp={mpp} units={opts.units} /></div>}
        {legendInCorner && <div style={corner(opts.legendPlacement)}><PrintLegend layers={layers} colorScales={colorScales} /></div>}
      </div>

      {side && (
        <div data-testid="print-side-column" style={{ position: 'absolute', left: side.x, top: side.y, width: side.w, height: side.h, overflow: 'hidden' }}>
          <PrintLegend layers={layers} colorScales={colorScales} />
        </div>
      )}

      {/* Footer */}
      <div style={{ position: 'absolute', left: 24, right: 24, bottom: 24, height: 36, borderTop: '1px solid #8b949e', paddingTop: 4, fontSize: 9, color: MUTED, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div>{opts.dateText}</div>
          <div>{crsText(crsLabel)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {capture && <div data-testid="scale-text">Scale {formatScale(denominator)} (at full page size)</div>}
          <div>{opts.projectName} · Ultimate-GIS · Advanced Geosciences, Inc.</div>
        </div>
      </div>
    </div>
  );
});

export default PrintPage;
